import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VerifiedIcon from '@mui/icons-material/Verified';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import { makeStyles } from '@mui/styles';
import { colors, theme } from '../../theme';
import { RoutineDetailsDataType, RoutineType, VerificationRoutineMaster } from '../../types/routine.types';
import { ItemType } from '../../types/common.types';
import { CustomButton } from '../shared/button/button';
import { ToolTip } from '../shared/tooltip/tooltip';
import { CreateRoutinePanel } from './create';
import { DocCertDialog } from './docCertDialog';
import { Loader } from '../shared/loader/loader';
import { CustomAlert } from '../shared/alert/alert';
import { msalInstance } from '../../config/msalConfig';
import { useAsyncOperation } from '../../utils/use-async-operation';
import {
	getRoutines,
	getVerRoutineMaster,
	updateVerificationRoutine,
} from '../../services/verification-routine-service';
import { RoutineDetailsDialog } from './routineDetails';
import DetailsIcon from '../../images/details.png';

export const testid = 'verification-routine';

const color = colors();
const themes = theme().typography;

export const VerificationRoutine = (): ReactElement => {
	const classes = useStyles();
	const isAdmin: boolean = localStorage.getItem('roles')?.includes('TT.ReAdmin') || false;
	const userAccount = msalInstance.getActiveAccount();
	const loggedInUser = userAccount ? `${userAccount.name?.split('(')[0]}| ${userAccount.username}` : '';

	const [routinesList, setRoutinesList] = useState<RoutineType[]>([]);
	const [documentList, setDocumentList] = useState<ItemType[]>([]);
	const [scopeCertList, setScopeCertList] = useState<ItemType[]>([]);
	const [routine, setRoutine] = useState<RoutineType>({} as RoutineType);
	const [updateDetails, setUpdateDetails] = useState<RoutineType>({} as RoutineType);
	const [isCreate, setIsCreate] = useState<boolean>(false);
	const [isDoc, setIsDoc] = useState<boolean>(false);
	const [openCreate, setOpenCreate] = useState<boolean>(false);
	const [openDialog, setOpenDialog] = useState<boolean>(false);
	const [openError, setOpenError] = useState(false);
	const [openSuccess, setOpenSuccess] = useState(false);
	const [openDetailsDialog, setOpenDetailsDialog] = useState<boolean>(false);
	const initialMasterLoadTriggered = useRef<boolean>(false);
	const initialRoutinesLoadTriggered = useRef<boolean>(false);

	const [getVerRoutines, verRoutineBusy, verRoutines, verRoutineError] = useAsyncOperation<RoutineType[]>(getRoutines);
	const [getMasterData, masterBusy, masterData, masterError] =
		useAsyncOperation<VerificationRoutineMaster>(getVerRoutineMaster);
	const [updateVerRoutine, updateVerRoutineBusy, updateVerRoutineData, updateVerRoutineError] =
		useAsyncOperation<RoutineType>(updateVerificationRoutine);

	useEffect(() => {
		if (!initialMasterLoadTriggered.current) {
			initialMasterLoadTriggered.current = true;
			getMasterData();
		}
		if (!initialRoutinesLoadTriggered.current) {
			initialRoutinesLoadTriggered.current = true;
			getVerRoutines();
		}
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (verRoutines)
			setRoutinesList(
				verRoutines.filter((r) => r.routineName !== 'NA') &&
					verRoutines.sort((a, b) => a.routineName.localeCompare(b.routineName))
			);
	}, [verRoutines]);

	useEffect(() => {
		if (verRoutineError || masterError) setOpenError(true);
	}, [verRoutineError, masterError]);

	useEffect(() => {
		if (masterData) {
			setDocumentList([
				...masterData.documentMaster.map((d) => {
					return { key: d.keyId, value: d.name };
				}),
				{
					key: -1,
					value: (
						<div className={classes.newDoc}>
							<AddIcon />
							Add a document
						</div>
					),
				},
			]);
			setScopeCertList([
				...masterData.scopeCertificates.map((d) => {
					return { key: d.certificateId, value: d.name };
				}),
			]);
		}
	}, [masterData]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (updateVerRoutineData) {
			setOpenDetailsDialog(false);
			getVerRoutines();
		}
	}, [updateVerRoutineData]); // eslint-disable-line react-hooks/exhaustive-deps

	const handleAdd = (index: number, isDoc: boolean) => {
		if (routinesList) setRoutine({ ...routinesList.filter((_, i) => i === index)[0], updatedBy: loggedInUser });
		setIsDoc(isDoc);
		setOpenDialog(true);
	};

	const handleUpdate = (isNewDoc: boolean) => {
		if (openCreate) setOpenCreate(false);
		else setOpenDialog(false);
		getVerRoutines();
		if (isNewDoc) getMasterData();
		setOpenSuccess(true);
	};

	const handleRoutineUpdate = (updatedRoutine: RoutineDetailsDataType) => {
		let saveData: RoutineType = {
			...updateDetails,
			routineDescription: updatedRoutine.routineDescription,
			compositionRequirement: updatedRoutine.compositionRequirement,
			blendingRestriction: updatedRoutine.blendingRestriction,
			sampleCertification: updatedRoutine.sampleCertification,
			certificationApplication: updatedRoutine.certificationApplication,
			routineDocuments: updatedRoutine.uploadedDocuments,
			allowMaterialEdits: updatedRoutine.allowMaterialEdits,
		};
		updateVerRoutine(saveData);
	};

	return (
		<>
			<Loader
				key={`${testid}-loader`}
				open={verRoutineBusy || masterBusy || updateVerRoutineBusy}
				message="Loading..."
			/>
			<CustomAlert
				open={openError}
				message="Something went wrong while retrieving verification routines data!"
				severity="error"
				timeout={5000}
				isFade={false}
				onCloseCallback={() => {
					setOpenError(false);
				}}
			/>
			{openCreate ? (
				<CreateRoutinePanel
					routineNames={routinesList?.map((r) => r.routineName.toLowerCase()) || []}
					certificateList={scopeCertList}
					documentList={documentList}
					loggedInUser={loggedInUser}
					onBackClick={setOpenCreate}
					onSaveClick={handleUpdate}
					data-testid={`${testid}-create`}
				/>
			) : (
				<>
					{isAdmin && (
						<div className={classes.button} data-testid={`${testid}-create-button`}>
							<CustomButton
								id="new"
								text="Create a new routine"
								width={180}
								variant="contained"
								onClickCallback={() => {
									setOpenCreate(true);
									setIsCreate(true);
								}}
							/>
						</div>
					)}
					<TableContainer
						className={classes.table}
						data-testid={`${testid}-body`}
						sx={{ height: `calc(100% - ${isAdmin ? '180px' : '130px'})` }}
					>
						<Table stickyHeader>
							<TableHead>
								<TableRow key={`${testid}-header`}>
									<TableCell width="25%">Routine Name</TableCell>
									<TableCell width="10%">Routine Details</TableCell>
									<TableCell width="44%">Required Documents to Submit</TableCell>
									<TableCell width="5%">Type</TableCell>
									<TableCell width="15%">Deadline</TableCell>
									{isAdmin && <TableCell width="1%" />}
								</TableRow>
							</TableHead>
							{routinesList && (
								<TableBody data-testid={`${testid}-body`}>
									{routinesList.map((row, i) => (
										<TableRow hover key={`${testid}-${row.id}`} data-testid={`${testid}-${row.id}`}>
											<TableCell>
												<div className={classes.routine}>
													{row.routineName}
													{row.scopeCertificates && row.scopeCertificates.length > 0 ? (
														<ToolTip
															tooltipText={
																<>
																	<b>Scope Certificate</b>
																	{row.scopeCertificates.map((s) => (
																		<p className={classes.flexDisplay} key={s.keyId}>
																			<div style={{ width: '15%' }}>{s.keyId}</div>
																			<div>{s.name}</div>
																		</p>
																	))}
																</>
															}
															width={300}
															children={
																<span className={classes.centerAlign}>
																	<VerifiedIcon className={classes.iconButton} id="scope-cert" />
																</span>
															}
														/>
													) : (
														<ToolTip
															tooltipText="Add scope certificate"
															children={
																<span className={classes.centerAlign}>
																	{isAdmin && (
																		<VerifiedOutlinedIcon
																			id="add-sc"
																			className={classes.iconButton}
																			onClick={() => handleAdd(i, false)}
																		/>
																	)}
																</span>
															}
														/>
													)}
												</div>
											</TableCell>
											<TableCell>
												<ToolTip
													tooltipText={isAdmin ? 'Update Routine Details' : 'View Routine Details'}
													children={
														<span>
															<IconButton
																id="update-deatils"
																onClick={() => {
																	setUpdateDetails(row);
																	setOpenDetailsDialog(true);
																}}
															>
																<img
																	src={DetailsIcon}
																	className={classes.detailsButton}
																	alt={isAdmin ? 'Update Routine Details' : 'View Routine Details'}
																/>
															</IconButton>
														</span>
													}
												/>
											</TableCell>
											<TableCell>{row.documents.map((d) => d.name).join(', ')}</TableCell>
											<TableCell>{row.type}</TableCell>
											<TableCell>{row.deadline}</TableCell>
											{isAdmin && (
												<TableCell>
													<ToolTip
														tooltipText="Add document"
														children={
															<span>
																<IconButton id="add-doc" onClick={() => handleAdd(i, true)}>
																	<AddIcon className={classes.iconButton} />
																</IconButton>
															</span>
														}
													/>
												</TableCell>
											)}
										</TableRow>
									))}
								</TableBody>
							)}
						</Table>
					</TableContainer>
				</>
			)}
			{openDialog && (
				<DocCertDialog
					isDoc={isDoc}
					data={routine}
					list={isDoc ? documentList : scopeCertList}
					onSaveClick={handleUpdate}
					onCancelClick={() => setOpenDialog(false)}
				/>
			)}
			{openSuccess && (
				<div className={classes.successAlert}>
					<CustomAlert
						open={openSuccess}
						timeout={5000}
						message={`${isCreate ? 'Routine' : isDoc ? 'Document' : 'Scope Certificate'} saved successfully!`}
						severity="success"
						onCloseCallback={() => {
							setOpenSuccess(false);
							setIsCreate(false);
						}}
					/>
				</div>
			)}
			{openDetailsDialog && updateDetails && updateDetails.id && (
				<RoutineDetailsDialog
					isAdmin={isAdmin}
					isMaterial={false}
					isError={!!updateVerRoutineError}
					data={
						{
							id: updateDetails.id,
							routineName: updateDetails.routineName,
							routineDescription: updateDetails.routineDescription,
							materialName: '',
							materialDescription: '',
							compositionRequirement: updateDetails.compositionRequirement,
							blendingRestriction: updateDetails.blendingRestriction,
							sampleCertification: updateDetails.sampleCertification,
							certificationApplication: updateDetails.certificationApplication,
							uploadedDocuments: updateDetails.routineDocuments,
							allowMaterialEdits: updateDetails.allowMaterialEdits,
						} as RoutineDetailsDataType
					}
					onSaveClick={(data) => {
						handleRoutineUpdate(data);
					}}
					onCancelClick={() => setOpenDetailsDialog(false)}
				/>
			)}
		</>
	);
};

const useStyles = makeStyles(() => ({
	flexDisplay: {
		display: 'flex',
		alignItems: 'center',
	},
	centerAlign: {
		verticalAlign: 'text-top',
	},
	button: {
		display: 'flex',
		justifyContent: 'end',
		'& #new': {
			backgroundColor: color.primary[100],
			color: color.text.light,
		},
	},
	table: {
		'& .MuiTableCell-head': {
			...themes.bodyMedium,
			color: color.neutral[8],
			backgroundColor: color.neutral[1],
			borderTop: `1px solid ${color.neutral[2]}`,
		},
		'& .MuiTableCell-body': {
			...themes.bodyRegular,
			padding: '3px 8px !important',
		},
		'& .MuiTableCell-root': {
			borderBottom: `1px solid ${color.neutral[2]}`,
			padding: '13px 8px',
		},
		'& .MuiTableRow-hover': {
			'&:hover': {
				backgroundColor: `${color.signal.infoLight} !important`,
				'& #add-sc': {
					display: 'block',
				},
			},
		},
	},
	routine: {
		display: 'ruby',
		'& svg': {
			marginLeft: '5px',
		},
		'& #add-sc': {
			display: 'none',
			cursor: 'pointer',
		},
	},
	iconButton: {
		width: '16px !important',
		height: '16px !important',
		color: color.primary[90],
	},
	detailsButton: {
		width: '30px',
		height: '30px',
	},
	newDoc: {
		display: 'flex',
		width: '100%',
		color: color.signal.info,
		borderTop: `1px solid ${color.neutral[4]}`,
		paddingTop: '10px',
	},
	successAlert: {
		position: 'fixed',
		top: '10px',
		left: '50%',
		transform: 'translateX(-50%)',
	},
}));
